import { useCallback, useEffect, useState } from "react";
import axios from "../../api/axios";
import useAuth from "../../hooks/useAuth";
import { AddImageButton, DeleteButton, SaveButton } from "../buttons/Buttons";
import imageCompression from "browser-image-compression";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import cloudinaryConfig from "../../config/cloudinary.config";

const WEBSITE_URL = "/image_url";

export default function PhotoCarouselEdit({ title }) {
  const galleryTitle = "gallery-" + title;
  const { username } = useAuth();

  const [imagesToUpload, setImagesToUpload] = useState([]);
  const [imageObjects, setImageObjects] = useState([]);
  const [lastIndex, setLastIndex] = useState(0);
  const [status, setStatus] = useState("");
  const MAX = 4;

  const loadGallery = useCallback(async () => {
    try {
      const res = await axios.get(WEBSITE_URL + "/gallery", {
        params: { title: galleryTitle, username }
      });

      if (res.data && res.data.length > 0) {
        const images = res.data.map((item, idx) => ({
          url: item.url,
          public_id: item.public_id,
          sortOrder: item.sortOrder ?? idx
        }));

        setImageObjects(images.sort((a, b) => a.sortOrder - b.sortOrder));

        const last = Math.max(
          ...res.data.map(obj =>
            Number(obj.public_id.split("-").slice(-1)[0])
          )
        );
        if (last) setLastIndex(last);
      }
    } catch {}
  }, [galleryTitle, username]);

  useEffect(() => {
    loadGallery();
  }, [loadGallery]);

  const sensors = useSensors(useSensor(PointerSensor));

  function SortableGridItem({ image }) {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: image.public_id });

    return (
      <div
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          cursor: "grab",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "8px"
        }}
      >
        <img src={image.url} className="gallery-image" alt="" />
        <DeleteButton
          id={image.public_id}
          buttonText="Delete"
          onClick={image.onDelete}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
      </div>
    );
  }

  function handleDragEnd(e) {
    const { active, over } = e;
    if (!over) return;

    if (active.id !== over.id) {
      setImageObjects(items => {
        const oldIndex = items.findIndex(i => i.public_id === active.id);
        const newIndex = items.findIndex(i => i.public_id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  async function handleUpload(event) {
    const files = event.target.files;
    if (files.length + imageObjects.length > MAX) {
      setStatus(`Max ${MAX} images allowed`);
      return;
    }
    setImagesToUpload([...files]);
  }

  async function uploadImages() {
    if (imagesToUpload.length === 0) return;

    setStatus("Uploading...");

    for (let i = 0; i < imagesToUpload.length; i++) {
      const file = imagesToUpload[i];
      const compressed = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920
      });

      const reader = new FileReader();
      const newIndex = lastIndex + i + 1;

      reader.readAsDataURL(compressed);
      reader.onload = async () => {
        const base64 = reader.result;

        const signRes = await axios.get("/signuploadform", {
          params: {
            folder: username + "/" + galleryTitle,
            publicID: `${title}-${newIndex}`
          }
        });

        const s = signRes.data;
        const url =
          cloudinaryConfig.BASE_URL + s.cloudName + cloudinaryConfig.UPLOAD_SUFFIX;

        const formData = new FormData();
        formData.append("api_key", s.apiKey);
        formData.append("cloud_name", s.cloudName);
        formData.append("file", base64);
        formData.append("folder", username + "/" + galleryTitle);
        formData.append("public_id", `${title}-${newIndex}`);
        formData.append("signature", s.signature);
        formData.append("timestamp", s.timestamp);

        const uploadRes = await axios.post(url, formData);

        await axios.put(WEBSITE_URL, {
          username,
          title: galleryTitle,
          url: uploadRes.data.secure_url,
          public_id: uploadRes.data.public_id
        });

        loadGallery();
        setStatus("Uploaded!");
      };
    }

    setImagesToUpload([]);
  }

  async function handleDelete(e) {
    const public_id = e.target.id;

    const signRes = await axios.get("/signuploadform", {
      params: { publicID: public_id }
    });

    const s = signRes.data;
    const url =
      cloudinaryConfig.BASE_URL + s.cloudName + cloudinaryConfig.DELETE_SUFFIX;

    await axios.post(url, {
      signature: s.signature,
      api_key: s.apiKey,
      cloud_name: s.cloudName,
      timestamp: s.timestamp,
      public_id,
      invalid: true
    });

    await axios.delete(WEBSITE_URL, {
      data: { username, title: galleryTitle, public_id }
    });

    loadGallery();
  }

  async function saveOrder() {
    const order = imageObjects.map((img, index) => ({
      public_id: img.public_id,
      sortOrder: index
    }));

    await axios.post(WEBSITE_URL + "/reorder", {
      username,
      title: galleryTitle,
      order
    });

    setStatus("Order saved!");
    loadGallery();
  }

  return (
    <div className="sugg-carousel">
      <AddImageButton
        buttonText="Add Image"
        disable={imageObjects.length >= MAX}
        onClick={() => document.getElementById("extra-carousel-upload").click()}
      />

      <input
        id="extra-carousel-upload"
        type="file"
        style={{ display: "none" }}
        onChange={handleUpload}
      />

      {imagesToUpload.length > 0 && (
        <SaveButton buttonText="Upload Selected" enabled={true} onClick={uploadImages} />
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={imageObjects.map(i => i.public_id)}
          strategy={verticalListSortingStrategy}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
              gap: "15px",
              marginTop: "20px"
            }}
          >
            {imageObjects.map(img => (
              <SortableGridItem
                key={img.public_id}
                image={{
                  ...img,
                  onDelete: handleDelete
                }}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div style={{ marginTop: "40px" }}>
        <SaveButton buttonText="Save Order" enabled={true} onClick={saveOrder} />
      </div>

      {status && <div className="status-info">{status}</div>}
    </div>
  );
}
